import { useApolloClient } from '@apollo/client';
import { useI18n } from '@i18n';
import { Box, Button, Card, Popover, Stack, Tab, Tabs, useTheme } from '@mui/material';
import Container from '@mui/material/Container';
import { consumption_source_type_enum, DataCollectionAreaConsumptionDraftFragment } from '@predium/client-graphql';
import { useSnackbar } from 'notistack';
import { useQueryStates } from 'nuqs';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ICONS } from '@/assets/icons';
import IconStylus from '@/assets/Icons/consumption/stylus';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import Iconify from '@/components/Iconify';
import { SnackbarTimeouts } from '@/components/NotistackProvider';
import AccessRightsWrapper from '@/components/permission-tooltips/AccessRightsWrapper';
import PreDialog from '@/components/presentations/PreDialog/PreDialog';
import { SemanticButton } from '@/components/Tile';
import { WarningModal } from '@/components/WarningModal';
import { GET_CONSUMPTION_DRAFTS, GET_CONSUMPTIONS } from '@/graphql/DataCollection.queries';
import DataCollectionSingleConsumptionDraftModal from '@/pages/DataCollection/DataCollectionSingleConsumptionDraftModal';
import { PATHS } from '@/routes';
import { SEARCH_PARAMS } from '@/routes/search-params';
import AreaConsumptionDraftList from '@/sections/DataCollection/Building/Consumption/AreaConsumptionDraftList';
import AreaConsumptionList from '@/sections/DataCollection/Building/Consumption/AreaConsumptionList';
import UploadConsumptionInvoiceDraftModal from '@/sections/DataCollection/Building/Consumption/UploadConsumptionDraftModal';
import useBuilding from '@/sections/DataCollection/Building/Context/useBuilding';

export enum ConsumptionTabNamesEnum {
  consumptions = 'consumptions',
  consumption_drafts = 'consumption_drafts',
}

export default function AreaConsumptionListTabs() {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useI18n();
  const apollo = useApolloClient();
  const theme = useTheme();
  const navigate = useNavigate();

  const { building } = useBuilding();

  const [uploadNewConsumptionDraftModalOpen, setUploadNewConsumptionDraftModalOpen] = useState(false);
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [consumptionSourceTypeToCreate, setConsumptionSourceTypeToCreate] =
    useState<consumption_source_type_enum | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [newConsumptionDraftAfterCreation, setNewConsumptionDraftAfterCreation] =
    useState<DataCollectionAreaConsumptionDraftFragment | null>(null);
  const anchorElementRef = useRef<HTMLDivElement | null>(null);
  const createdConsumptionDrafts = useRef<{ id: number }[]>([]);

  const { hasEditAccess } = useBuilding();

  const tabs = [
    {
      label: t('DataCollection_ConsumptionHistory'),
      value: 'consumptions',
      component: <AreaConsumptionList building={building} />,
    },
    ...(hasEditAccess
      ? [
          {
            label: t('DataCollection_ConsumptionsInReview'),
            value: 'consumption_drafts',
            component: <AreaConsumptionDraftList buildingId={building.id} />,
          },
        ]
      : []),
  ];

  const [queryParams, setQueryParams] = useQueryStates(SEARCH_PARAMS.dataCollection.building);

  useEffect(() => {
    if (!queryParams.consumptionsTab) {
      void setQueryParams({ consumptionsTab: ConsumptionTabNamesEnum.consumptions });
    }
  }, [queryParams.consumptionsTab, setQueryParams]);

  const handleChange = (_: React.SyntheticEvent, consumptionsTab: ConsumptionTabNamesEnum) => {
    void setQueryParams({
      consumptionsTab,
    });
  };

  const refetchAreaData = async () => {
    await apollo.refetchQueries({
      include: [GET_CONSUMPTIONS, GET_CONSUMPTION_DRAFTS],
    });
  };

  const onConsumptionDraftAdded = async (consumptionDraftIds: number[]) => {
    refetchAreaData();

    setUploadNewConsumptionDraftModalOpen(false);
    createdConsumptionDrafts.current = [];

    enqueueSnackbar(t('General_Upload-success'), {
      variant: 'success',
      autoHideDuration: SnackbarTimeouts.Success,
    });

    if (consumptionDraftIds.length === 1) {
      navigate(
        PATHS.dataCollection.buildingConsumptionInvoiceDraft({
          id: building.id,
          draftId: consumptionDraftIds[0],
        }),
      );
    } else {
      void setQueryParams({ consumptionsTab: ConsumptionTabNamesEnum.consumption_drafts });
    }
  };

  return (
    <Container maxWidth={'lg'} sx={{ mb: 4 }}>
      <Stack direction="row" justifyContent="space-between" sx={{ width: '100%', mb: 2 }}>
        <Tabs value={queryParams.consumptionsTab} variant="standard" onChange={handleChange}>
          {tabs.map((tab) => (
            <Tab disableRipple key={tab.value} label={tab.label} value={tab.value} />
          ))}
        </Tabs>

        <AccessRightsWrapper hasAccess={hasEditAccess}>
          <div ref={anchorElementRef as any}>
            <Button variant="contained" onClick={() => setOpenAddModal(true)} startIcon={<Iconify icon={ICONS.ADD} />}>
              {t('DataCollection_AddNewConsumption-button')}
            </Button>
          </div>
        </AccessRightsWrapper>
      </Stack>

      <Popover
        open={openAddModal}
        anchorEl={anchorElementRef.current}
        onClose={() => setOpenAddModal(false)}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <Card sx={{ maxWidth: '500px' }}>
          <Stack>
            <SemanticButton
              sx={{
                padding: 8,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&:focus': {
                  backgroundColor: theme.palette.action.focus,
                },
              }}
              onClick={() => {
                setUploadNewConsumptionDraftModalOpen(true);
                setOpenAddModal(false);
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Iconify icon={ICONS.FILE_UPLOAD_OUTLINE} width={20} height={20} color={theme.palette.grey[600]} />
                <PTypography variant="body1" align="left">
                  <strong>{t('DataCollection_DraftsUploadInvoice-title')}</strong>
                </PTypography>
              </Box>

              <PTypography variant="body1" align="left" color="secondary">
                {t('DataCollection_DraftsUploadInvoice-description')}
              </PTypography>
            </SemanticButton>
            <SemanticButton
              sx={{
                padding: 8,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&:focus': {
                  backgroundColor: theme.palette.action.focus,
                },
              }}
              onClick={() => {
                setConsumptionSourceTypeToCreate(consumption_source_type_enum.MANUAL_ENTRY);
                setIsEditDialogOpen(true);
                setOpenAddModal(false);
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Iconify icon={ICONS.GAUGE} width={20} height={20} color={theme.palette.grey[600]} />
                <PTypography variant="body1" align="left">
                  <strong>{t('DataCollection_DraftsUploadManualEntry-title')}</strong>
                </PTypography>
              </Box>

              <PTypography variant="body1" align="left" color="secondary">
                {t('DataCollection_DraftsUploadManualEntry-description')}
              </PTypography>
            </SemanticButton>
            <SemanticButton
              sx={{
                padding: 8,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&:focus': {
                  backgroundColor: theme.palette.action.focus,
                },
              }}
              onClick={() => {
                setConsumptionSourceTypeToCreate(consumption_source_type_enum.ESTIMATION);
                setIsEditDialogOpen(true);
                setOpenAddModal(false);
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <IconStylus />
                <PTypography variant="body1" align="left">
                  <strong>{t('DataCollection_DraftsUploadEstimation-title')}</strong>
                </PTypography>
              </Box>

              <PTypography variant="body1" align="left" color="secondary">
                {t('DataCollection_DraftsUploadEstimation-description')}
              </PTypography>
            </SemanticButton>
          </Stack>
        </Card>
      </Popover>

      {/* Create new single consumption draft */}
      <DataCollectionSingleConsumptionDraftModal
        buildingId={building.id}
        isOpen={Boolean(isEditDialogOpen)}
        consumptionSourceType={consumptionSourceTypeToCreate!}
        onClose={() => {
          setIsEditDialogOpen(false);
          setNewConsumptionDraftAfterCreation(null);
          // Set it delayed to avoid flickering
          setTimeout(() => {
            setConsumptionSourceTypeToCreate(null);
          }, 250);
        }}
        consumptionDraftData={newConsumptionDraftAfterCreation}
        setConsumptionDraftData={setNewConsumptionDraftAfterCreation}
      />

      {/* Create new consumption invoice draft */}
      <PreDialog
        type="definedByChildren"
        open={uploadNewConsumptionDraftModalOpen}
        fullWidth
        onClose={() => {
          setUploadNewConsumptionDraftModalOpen(false);
          if (createdConsumptionDrafts.current.length > 0) {
            // only change tab when upload was created and the dialog wasn't closed immediately.
            void setQueryParams({ consumptionsTab: ConsumptionTabNamesEnum.consumption_drafts });
            setWarningModalOpen(true);
            createdConsumptionDrafts.current = [];
          }
          refetchAreaData();
        }}
      >
        <UploadConsumptionInvoiceDraftModal
          buildingId={building.id}
          onConsumptionDraftAdded={onConsumptionDraftAdded}
          createdConsumptionDrafts={createdConsumptionDrafts}
        />
      </PreDialog>

      <WarningModal
        title={t('General_Note')}
        open={warningModalOpen}
        description={t('DataCollection_DraftsUpload_WarningMessage')}
        onAcknowledge={() => {
          setWarningModalOpen(false);
        }}
        onClose={() => {
          setWarningModalOpen(false);
          void setQueryParams({ consumptionsTab: ConsumptionTabNamesEnum.consumption_drafts });
        }}
        buttonText={t('General_Ok')}
      />

      <Box sx={{ mb: 5 }} />

      {tabs.map((tab) => {
        const isMatched = tab.value === queryParams.consumptionsTab;
        return isMatched && <Box key={tab.value}>{tab.component}</Box>;
      })}
    </Container>
  );
}
